import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert,
  TextField, Autocomplete, Checkbox, CircularProgress, Box,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import axios from "../../../config/axiosConfig";

const formatFecha = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

// Reemplaza carga_factura.php + guarda_factura.php: primero se elige el
// cliente-servicio (igual al <select> "factura" de modal.php), después se listan sus
// facturas para tildar con checkbox — reusa facturaModel.getDetalleServicioFacturas
// (mismo endpoint que ya sirve el módulo de Facturación).
const AgregarFacturaDialog = ({ open, onClose, onAgregar }) => {
  const [cliente, setCliente] = useState(null);
  const [clienteInput, setClienteInput] = useState("");
  const [opcionesCliente, setOpcionesCliente] = useState([]);
  const [buscandoCliente, setBuscandoCliente] = useState(false);

  const [facturas, setFacturas] = useState([]);
  const [loadingFacturas, setLoadingFacturas] = useState(false);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setCliente(null);
    setClienteInput("");
    setOpcionesCliente([]);
    setFacturas([]);
    setSeleccionados(new Set());
    setError("");
  }, [open]);

  useEffect(() => {
    if (!clienteInput || clienteInput.trim().length < 2) {
      setOpcionesCliente([]);
      return;
    }
    setBuscandoCliente(true);
    const timer = setTimeout(() => {
      axios.get("/api/proyectos/buscar-clientes", { params: { q: clienteInput } })
        .then((res) => setOpcionesCliente(res.data?.data || []))
        .catch(() => setOpcionesCliente([]))
        .finally(() => setBuscandoCliente(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [clienteInput]);

  useEffect(() => {
    if (!cliente) {
      setFacturas([]);
      return;
    }
    setLoadingFacturas(true);
    setSeleccionados(new Set());
    axios.get("/api/proyectos/buscar-facturas", { params: { cod_servicio_cliente: cliente.cod_servicio_cliente } })
      .then((res) => setFacturas(res.data?.data?.facturas || []))
      .catch(() => setFacturas([]))
      .finally(() => setLoadingFacturas(false));
  }, [cliente]);

  const toggleFila = (cod) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(cod)) next.delete(cod);
      else next.add(cod);
      return next;
    });
  };

  const handleAgregar = async () => {
    setSaving(true);
    setError("");
    try {
      await onAgregar(Array.from(seleccionados));
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || "Error al agregar las facturas.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Agregar Facturas</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <Autocomplete
          size="small"
          options={opcionesCliente}
          loading={buscandoCliente}
          value={cliente}
          inputValue={clienteInput}
          filterOptions={(x) => x}
          getOptionLabel={(o) => (o ? `${o.razon_social} / ${o.descripcion}` : "")}
          isOptionEqualToValue={(o, v) => o.cod_servicio_cliente === v?.cod_servicio_cliente}
          onChange={(_, value) => setCliente(value)}
          onInputChange={(_, value, reason) => {
            if (reason === "input") setClienteInput(value);
          }}
          sx={{ mb: 1.5 }}
          renderInput={(params) => (
            <TextField
              {...params} label="Buscar Cliente" autoFocus
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {buscandoCliente && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {loadingFacturas ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>
        ) : !cliente ? null : facturas.length === 0 ? (
          <Alert severity="info">Este cliente no tiene facturas.</Alert>
        ) : (
          <TableContainer sx={{ maxHeight: "40vh" }}>
            <Table size="small" stickyHeader sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>N° Factura</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="right">Bs.</TableCell>
                  <TableCell align="right">$us.</TableCell>
                  <TableCell align="right">Saldo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {facturas.map((f) => (
                  <TableRow key={f.cod} hover onClick={() => toggleFila(f.cod)} sx={{ cursor: "pointer" }}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={seleccionados.has(f.cod)} />
                    </TableCell>
                    <TableCell>{f.num_fac || "-"}</TableCell>
                    <TableCell>{formatFecha(f.fecha_fac)}</TableCell>
                    <TableCell>{f.pre_fac === "si" ? "Pre-Factura" : "Factura"}</TableCell>
                    <TableCell align="right">{Number(f.monto_total).toFixed(2)}</TableCell>
                    <TableCell align="right">{Number(f.dolares).toFixed(2)}</TableCell>
                    <TableCell align="right">{Number(f.saldo).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleAgregar} disabled={saving || seleccionados.size === 0}>
          {saving ? "Guardando..." : `Agregar (${seleccionados.size})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgregarFacturaDialog;
